import { Navbar, Footer } from './LandingPage';
import { WanderfulHero } from './WanderfulHero';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AboutPage = () => {
  const navigate = useNavigate();
  return (
    <div className="bg-white min-h-screen font-sans">
      <Navbar />
      <WanderfulHero 
        title="We are Kuberya-AI Solutions."
        subtitle="A premier technology startup building enterprise-grade artificial intelligence, automated evaluation engines, and conversational SaaS systems."
        cta1Text="Our Capabilities"
        cta1Link="#capabilities"
      />

      {/* About Section */}
      <section id="capabilities" className="py-8 sm:py-12 md:py-24 bg-white relative overflow-hidden">
        {/* Subtle decorative grid background */}
        <div className="absolute inset-0 opacity-[0.03] bg-[linear-gradient(to_right,#000_1px,transparent_1px),linear-gradient(to_bottom,#000_1px,transparent_1px)] bg-[size:30px_30px]" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16 sm:mb-24">
            <span className="text-xs font-bold text-black uppercase tracking-[0.25em] bg-black/5 px-4 py-2 rounded-full border border-black/10">About Us</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-black leading-tight mt-6 tracking-tight">
              Evolving the way <br />
              <span className="text-gray-500">India Learns & Operates.</span>
            </h2>
          </div>

          {/* Main Content Grid (Borderless Minimalist Layout) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 lg:gap-16 items-center">
            
            {/* Left side: What we do (Borderless typography) */}
            <div className="lg:col-span-7 flex flex-col justify-center">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-black tracking-tight leading-tight mb-6">
                We build tools that automate your most tedious work.
              </h3>

              <p className="text-gray-500 mb-10 leading-relaxed text-lg font-medium">
                Kuberya-AI builds custom software and AI engines that do the heavy lifting for you. We focus on simplicity, accuracy, and speed.
              </p>

              {/* Clean bullet-style items */}
              <div className="space-y-4 sm:space-y-6 mb-12">
                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 rounded-full bg-black flex-shrink-0" />
                  <p className="font-extrabold text-black text-lg">Instant Exam Grading</p>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 rounded-full bg-black flex-shrink-0" />
                  <p className="font-extrabold text-black text-lg">Automated Office Workflows</p>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 rounded-full bg-black flex-shrink-0" />
                  <p className="font-extrabold text-black text-lg">100% Custom Tailored Rules</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <button onClick={() => navigate('/support')} className="bg-black hover:bg-gray-800 text-white font-extrabold py-4 px-8 rounded-2xl flex items-center justify-center gap-2 group transition-all duration-300 w-full sm:w-auto shadow-md">
                  Our Journey <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <a href="/support" className="text-black/60 hover:text-black text-sm font-bold transition-colors py-2 px-4">
                  Talk to our team →
                </a>
              </div>
            </div>

            {/* Right side: Sleek 2x2 Coordinate Grid (No Cards) */}
            <div className="lg:col-span-5 grid grid-cols-2 border-t border-l border-gray-200">
               {[
                 { label: "Sheets Graded", value: "1M+" },
                 { label: "Partner Institutions", value: "50+" },
                 { label: "Hours Saved", value: "15k+" },
                 { label: "Cost Reduction", value: "50%" }
               ].map((stat, i) => (
                 <div key={i} className="p-4 sm:p-6 md:p-8 border-r border-b border-gray-200 flex flex-col justify-center min-h-[160px] hover:bg-gray-50 transition-colors duration-300">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">{stat.label}</span>
                    <h4 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-black">{stat.value}</h4>
                 </div>
               ))}
            </div>

          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 sm:py-24 md:py-32 bg-black text-white rounded-t-[80px] -mt-10 relative z-10">
         <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold mb-8 tracking-tight">Our Philosophy</h2>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed mb-12 max-w-3xl mx-auto font-medium">
              We believe in transitioning artificial intelligence from a "black box" to a force of absolute transparency. Whether optimizing complex enterprise operations or evaluation workflows, our mission is to build highly reliable, compliant technology that frees human potential to focus on growth and mentoring.
            </p>
            <div className="w-20 h-1 bg-white/20 mx-auto rounded-full"></div>
         </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
